import { useEffect, useRef, useState } from "react";
import cn from "classnames";

import DatePicker, { type PopupPosition } from "./DatePicker";
import { type GenericFiltersMap } from "utils/filterUtils";
import { validDateFilter } from "utils/filterUtils";
import ValidationErrorPrompt from "components/forms/ValidationErrorPrompt";
import { type ErrorPosition, ErrorPositions } from "enums/errorPositions";

import "./filterDatePicker.scss";

type DateRangeFilter = {
  from: string;
  to: string;
  [key: string]: string;
};

interface FilterDatePickerProps<T extends string> {
  filter: GenericFiltersMap<T>;
  updateFilter: (filter: GenericFiltersMap<T>) => void;
  propertyName: T;
  placeholder: string;
  verticalPosition?: "top" | "bottom";
  horizontalPosition?: "auto" | "left" | "right";
  disabled?: boolean;
  popupPosition?: PopupPosition;
  minDate?: string;
  maxDate?: string;
  onValidationChange?: (propertyName: T, isValid: boolean) => void;
  errorPosition?: ErrorPosition;
}

function FilterDatePicker<T extends string>({
  filter,
  propertyName,
  placeholder,
  disabled,
  verticalPosition,
  horizontalPosition,
  popupPosition,
  minDate,
  maxDate,
  updateFilter,
  onValidationChange,
  errorPosition = ErrorPositions.top,
}: FilterDatePickerProps<T>) {
  const [isValid, setIsValid] = useState(true);
  const r = useRef<HTMLDivElement>(null);

  const handleFilterChange = (_: unknown, { value }: { value: string }) => {
    const isDateValid = validDateFilter(value);
    setIsValid(isDateValid);

    onValidationChange?.(propertyName, isDateValid);

    let newFilter = { ...filter };
    const lowerPlaceholder = placeholder.toLowerCase() as "from" | "to";

    if (value.length !== 0) {
      if (!newFilter[propertyName]) {
        newFilter[propertyName] = {} as DateRangeFilter;
      }

      const currentFilter = newFilter[propertyName] as DateRangeFilter;
      const updatedRangeFilter = {
        ...currentFilter,
        [lowerPlaceholder]: value,
      };

      newFilter = {
        ...newFilter,
        [propertyName]: updatedRangeFilter,
      };
    } else {
      const currentFilter = newFilter[propertyName] as DateRangeFilter;
      if (currentFilter) {
        delete currentFilter[lowerPlaceholder];

        if (!currentFilter.to && !currentFilter.from) {
          delete newFilter[propertyName];
        }
      }
    }

    updateFilter(newFilter);
  };

  const filterValue = filter[propertyName] as DateRangeFilter | undefined;
  const lowerPlaceholder = placeholder.toLowerCase();
  const value = filterValue ? filterValue[lowerPlaceholder] || "" : "";

  useEffect(() => {
    const isDateValid = validDateFilter(value);
    setIsValid(isDateValid);

    onValidationChange?.(propertyName, isDateValid);
  }, [value, propertyName, onValidationChange]);

  return (
    <div ref={r} className={cn("filter-date-picker-wrapper", { "has-error": !isValid })}>
      <DatePicker
        value={value}
        placeholder={placeholder}
        onChangeDate={handleFilterChange}
        disabled={disabled}
        verticalPosition={verticalPosition}
        horizontalPosition={horizontalPosition}
        popupPosition={popupPosition}
        minDate={minDate}
        maxDate={maxDate}
      />
      {!isValid && value && (
        <ValidationErrorPrompt innerRef={r} errorPosition={errorPosition} text="Date format must be mm/dd/yyyy" />
      )}
    </div>
  );
}

export default FilterDatePicker;
